/**
 * In-app confirmation and short-answer dialogs, used instead of the browser's
 * confirm() and prompt(). Call `ask` or `askText` from anywhere; one host mounted
 * at the root shows them.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Base = { title: string; description?: string; confirmLabel?: string; cancelLabel?: string; destructive?: boolean };
type Pending =
  | (Base & { type: "confirm"; resolve: (v: boolean) => void })
  | (Base & TextOpts & { type: "text"; resolve: (v: string | null) => void });
/** `validate` returns why the answer can't be used, or null; `defaultValue` starts the field filled in. */
type TextOpts = { label: string; placeholder?: string; required?: boolean; defaultValue?: string; validate?: (v: string) => string | null };

let current: Pending | null = null;
const listeners = new Set<() => void>();
const set = (p: Pending | null) => {
  current = p;
  listeners.forEach((l) => l());
};

/** Asks the user to confirm; resolves true only if they confirm. */
export function ask(opts: Base): Promise<boolean> {
  current?.type === "confirm" ? current.resolve(false) : current?.resolve(null);
  return new Promise((resolve) => set({ ...opts, type: "confirm", resolve }));
}

/** Asks for a short written answer; resolves null if cancelled. */
export function askText(opts: Base & TextOpts): Promise<string | null> {
  current?.type === "confirm" ? current.resolve(false) : current?.resolve(null);
  return new Promise((resolve) => set({ ...opts, type: "text", resolve }));
}

export function DialogHost() {
  const p = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
  const [text, setText] = useState("");
  useEffect(() => setText(p?.type === "text" ? (p.defaultValue ?? "") : ""), [p]);
  if (!p) return null;
  const close = (ok: boolean) => {
    set(null);
    if (p.type === "confirm") p.resolve(ok);
    else p.resolve(ok ? text.trim() : null);
  };
  const invalid = p.type === "text" && p.validate && text.trim() ? p.validate(text.trim()) : null;
  const blocked = (p.type === "text" && p.required && !text.trim()) || !!invalid;
  return (
    <Dialog open onOpenChange={(o) => !o && close(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{p.title}</DialogTitle>
          {p.description && <DialogDescription>{p.description}</DialogDescription>}
        </DialogHeader>
        {p.type === "text" && (
          <div className="space-y-1.5">
            <Label htmlFor="dialog-text">{p.label}</Label>
            <Textarea id="dialog-text" autoFocus rows={3} placeholder={p.placeholder} value={text} aria-invalid={!!invalid || undefined} aria-describedby={invalid ? "dialog-text-error" : undefined} onChange={(e) => setText(e.target.value)} />
            {invalid && (
              <p id="dialog-text-error" className="text-xs text-destructive">
                {invalid}
              </p>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => close(false)}>
            {p.cancelLabel ?? "Cancel"}
          </Button>
          <Button variant={p.destructive ? "destructive" : "default"} disabled={blocked} onClick={() => close(true)} autoFocus={p.type === "confirm"}>
            {p.confirmLabel ?? "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
